<template>
  <v-card outlined class="pa-4">
    <div class="d-flex align-start mb-3" style="gap: 12px;">
      <div>
        <div class="pw-section">{{ isMrr ? 'MRR' : 'Stock de membresías' }} {{ year }}</div>
        <div class="caption grey--text text--darken-1">
          {{ isMrr ? 'MRR en USD de las membresías activas al cierre de cada mes' : 'Activas al cierre de cada mes' }},
          apiladas por línea de negocio
        </div>
      </div>
      <v-spacer />
      <v-btn-toggle v-model="metric" mandatory dense color="primary">
        <v-btn value="count" small>Membresías</v-btn>
        <v-btn value="mrr" small>MRR</v-btn>
      </v-btn-toggle>
    </div>
    <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px;">
      <v-select
        v-model="line"
        :items="lineOptions"
        label="Línea de negocio"
        outlined
        dense
        hide-details
        class="line-filter"
      />
      <v-switch v-model="bciOnly" label="Solo BCI" inset dense hide-details class="mt-0" />
    </div>

    <div v-if="loading" class="d-flex justify-center py-10">
      <v-progress-circular indeterminate color="primary" />
    </div>
    <div v-else-if="!months.length" class="text-center grey--text py-10">Sin datos para {{ year }}</div>
    <template v-else>
      <!-- vue-chartjs mete su propio contenedor y, sin `styles`, el canvas usa su alto por
           defecto (400px) y se sale de la tarjeta. -->
      <area-chart :chart-data="chartData" :options="chartOptions" :styles="chartStyles" />
      <p class="caption grey--text text--darken-1 mt-3 mb-0">
        Haz clic en un mes para ver las altas y bajas del mes de B2C, B2B2C y Upsell.
        <span v-if="hasApproximate">* Meses aproximados: el historial de estados está completo solo desde julio de 2026.</span>
        <span v-if="isMrr" class="ml-1">El MRR de cada mes usa los precios y el tipo de cambio de hoy: muestra la cartera de ese mes, no el MRR que se reportó entonces.</span>
      </p>
    </template>
  </v-card>
</template>

<script>
import { Line } from 'vue-chartjs';
import { TREND_COLORS } from '@/components/B2CTrendChart.vue';
import { formatNumber, signed } from '@/utils/b2c-report.utils';

const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

// Las tres líneas que, apiladas, suman el total B2C/B2B2C. Loyalty no entra.
export const STACK_LINES = [
  { key: 'b2c', label: 'B2C', color: TREND_COLORS.primary },
  { key: 'b2b2c', label: 'B2B2C', color: TREND_COLORS.muted },
  { key: 'upsell', label: 'Upsell', color: TREND_COLORS.faint }
];

// Relleno translúcido de cada área, para que se lea la línea de borde encima.
const AREA_ALPHA = 0.35;

const hexToRgba = (hex, alpha) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const AreaChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data._chart) this.$data._chart.destroy();
        this.renderChart(newData, this.options);
      }
    }
  }
};

export default {
  name: 'B2CV2StockChart',
  components: { AreaChart },
  props: {
    year: { type: Number, default: null },
    // [{ month: 'YYYY-MM', approximate, series: { b2c, b2b2c, upsell, b2cBci, b2b2cBci, upsellBci } }]
    // Cada serie: { activations, churns, net, activeEnd, mrrUsd }.
    months: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false }
  },
  data() {
    return { line: 'all', bciOnly: false, metric: 'count' };
  },
  computed: {
    isMrr() {
      return this.metric === 'mrr';
    },
    lineOptions() {
      return [{ text: 'Todas', value: 'all' }, ...STACK_LINES.map((l) => ({ text: l.label, value: l.key }))];
    },
    shownLines() {
      return this.line === 'all' ? STACK_LINES : STACK_LINES.filter((l) => l.key === this.line);
    },
    hasApproximate() {
      return this.months.some((m) => m.approximate);
    },
    chartStyles() {
      return { position: 'relative', height: '320px', width: '100%' };
    },
    chartData() {
      return {
        labels: this.months.map((m) => {
          const label = MONTH_LABELS[Number(m.month.slice(5, 7)) - 1];
          return m.approximate ? `${label}*` : label;
        }),
        // La primera área parte del eje y cada una rellena hasta la anterior: el borde de
        // arriba es el total del mes.
        datasets: this.shownLines.map((l, i) => ({
          label: this.bciOnly ? `${l.label} (BCI)` : l.label,
          data: this.months.map((m) => this.valueOf(this.flowsOf(m, l.key))),
          borderColor: l.color,
          backgroundColor: hexToRgba(l.color, AREA_ALPHA),
          pointBackgroundColor: l.color,
          pointRadius: 4,
          pointHoverRadius: 6,
          borderWidth: 2,
          fill: i === 0 ? 'origin' : '-1',
          lineTension: 0.3
        }))
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: this.shownLines.length > 1, position: 'bottom', labels: { usePointStyle: true, padding: 20 } },
        // Por columna: un clic en cualquier altura del mes lo elige.
        hover: { mode: 'index', intersect: false },
        onHover: (event, elements) => {
          event.target.style.cursor = elements.length ? 'pointer' : 'default';
        },
        onClick: (event, elements) => this.selectMonth(elements),
        scales: {
          xAxes: [{ stacked: true, gridLines: { display: false } }],
          yAxes: [{
            stacked: true,
            ticks: { beginAtZero: true, maxTicksLimit: 6, callback: (v) => this.format(v) },
            gridLines: { color: 'rgba(0,0,0,0.05)' }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items) => {
              const m = this.months[items[0].index];
              return m.approximate ? `${items[0].label.replace('*', '')} (aproximado)` : items[0].label;
            },
            label: (item) => {
              const l = this.shownLines[item.datasetIndex];
              const f = this.flowsOf(this.months[item.index], l.key);
              if (this.isMrr) return `${l.label}: ${this.format(f.mrrUsd)} (${formatNumber(f.activeEnd)} membresías)`;
              return `${l.label}: ${formatNumber(f.activeEnd)} (neto del mes ${signed(f.net)})`;
            },
            footer: (items) => `Total: ${this.format(items.reduce((sum, i) => sum + Number(i.value), 0))}`
          }
        }
      };
    }
  },
  methods: {
    /** Avisa qué mes se eligió, con el filtro BCI vigente; la lista trae todas las líneas. */
    selectMonth(elements) {
      if (!elements.length) return;
      const [element] = elements;
      const month = this.months[element._index];
      if (!month) return;
      this.$emit('select-bar', { month: month.month, bciOnly: this.bciOnly });
    },
    /** Lo que mide el gráfico: cantidad de membresías activas o su MRR en USD. */
    valueOf(flows) {
      return this.isMrr ? flows.mrrUsd : flows.activeEnd;
    },
    format(value) {
      return this.isMrr ? `USD ${formatNumber(value)}` : formatNumber(value);
    },
    /** Serie de una línea, o su parte BCI cuando el filtro "Solo BCI" está activo. */
    flowsOf(month, lineKey) {
      return month.series[this.bciOnly ? `${lineKey}Bci` : lineKey];
    }
  }
};
</script>

<style scoped>
.line-filter {
  max-width: 200px;
}
</style>
